// NPM imports
import { jwtDecode, JwtPayload } from "jwt-decode";

// Imports from other pnpm packages
import {
  type Scopes,
  type AccessTokenExchangeOptions,
  type AccessTokenClaims,
} from "@securityjourney/frontend-lib-svc";

// Constants
import { AFTER_SIGN_OUT_PATH, SIGNED_OUT_PAGE } from "../constants/urls";
import { LoginAppRoutes } from "../constants/routes";

// Toolkit imports
import { getCognitoJwt, cognitoGlobalSignOut } from "../integrations/cognito/cognito";
import { getAuthClient, getIdpClient, getLoginClient } from "../client";

// Other utility imports
import {
  cleanUserArtifacts,
  getIsSjAdminFromStorage,
  getImpersonatedEmail,
  getCurrentSelectedTenant,
} from "./user";
import { isGuestAccessMode, getGuestAccessKeys, fetchNewScormToken } from "./guest";
import { getUsingCognitoCookies } from "./redirectStorage";

// Types for decoded JWT token claims
export interface DecodedAuthToken extends JwtPayload, AccessTokenClaims {}

// Methods for saving/retrieving the authToken from local storage
export const LocalTokenStorage = {
  save: (token: string) => {
    if (!token) {
      localStorage.removeItem("authToken");
      return;
    }
    localStorage.setItem("authToken", token);
  },
  get: () => localStorage.getItem("authToken"),
};

/**
 * Called when the app is first loaded to determine if the user is already authenticated.
 * It does this by attempting to retrieve the authToken from local storage and checking if it is expired.
 * If it is expired, it will attempt to exchange the Cognito token for a new authToken.
 * If successful, the new token is saved to local storage and the user is considered authenticated.
 * If unsuccessful (most likely because the Cognito refresh token is expired), the user is considered unauthenticated.
 * @returns boolean
 */
export const rehydrateUserAuthStatus = async () => {
  try {
    const token = (await retrieveAuthToken({ noLogOut: true })) as string;
    const isAuthenticated = !!token && !isTokenExpired(token);
    return isAuthenticated;
  } catch (_e) {
    return false;
  }
};

/**
 * Retrieve the authToken. Handles initial token generation (via Cognito JWT exchange)
 * as well as refreshing the token when if it has expired.

 * @param {boolean} forceRefresh Pass true to force a refresh of the token 
 * @returns {Promise<string>} The authToken
 */
export const retrieveAuthToken = async ({
  forceRefresh = false,
  noLogOut = false,
} = {}): Promise<string> => {
  // First, check to see if we have an unexpired auth token.
  let token = LocalTokenStorage.get();

  // If necessary, get a new token
  if (forceRefresh || !token || !isTokenValid(token)) {
    token = await getNewAuthToken({ forceRefresh, noLogOut });
  }

  return token;
};

/**
 * Wrapper around retrieveAuthToken to always return decoded token
 */
export const retrieveDecodedAuthToken = async ({
  forceRefresh = false,
  noLogOut = false,
} = {}): Promise<DecodedAuthToken> => {
  const encodedToken = await retrieveAuthToken({ forceRefresh, noLogOut });

  return jwtDecode<DecodedAuthToken>(encodedToken);
};

/**
 * Does the token exchange. Regular access uses Cognito token exchange.
 * Guest access exchanges SCORM access keys to get a token.
 */
const getNewAuthToken = async (
  options: AccessTokenExchangeOptions = { forceRefresh: false, scopes: null, noLogOut: false }
) => {
  let token: string = "";
  if (isGuestAccessMode()) {
    // Guest access - use SCORM token exchange
    token = await fetchNewScormToken();
  } else {
    // Regular access - use Cognito token exchange
    token = await exchangeCognitoTokenForSvcToken(options);
  }

  // Save the new token
  LocalTokenStorage.save(token);

  return token;
};

/**
 * Check if the token is valid (exists, not expired,
 * and for guest access, matches lesson key)
 */
const isTokenValid = (token: string) => {
  try {
    const claims = jwtDecode(token);

    // Expired -> invalid
    if (claims.exp) {
      const curr = Math.floor(Date.now() / 1000);
      if (claims.exp < curr) {
        return false;
      }
    }

    // If guest, make sure that the lesson key in the token matches the URL
    const guestKeys = getGuestAccessKeys();
    if (guestKeys) {
      const tokenLessonKey = (claims as any).lesson_key;
      if (tokenLessonKey !== guestKeys.lessonKey) {
        return false;
      }
    }

    return true;
  } catch (_e) {
    return false;
  }
};

export const retrieveAuthenticatedUserInfo = () => {
  const svcToken = LocalTokenStorage.get();
  if (!svcToken || svcToken === "null") {
    return null;
  }
  const decodedToken = jwtDecode<JwtPayload & AccessTokenClaims>(svcToken);

  // If any required fields are missing, return null
  if (!decodedToken.email || !decodedToken.directory_uuid) {
    return null;
  }

  return {
    email: decodedToken.email,
    userId: decodedToken.directory_uuid,
    tenants: decodedToken.available_tenants || [],
  };
};

/**
 * Switches the auth scopes for the current user.
 * This is used when a user switches tenants or impersonates another user.
 * @param {Object} scopes
 */
export const switchAuthScopes = async (scopes: Scopes) => {
  const newToken = await exchangeCognitoTokenForSvcToken({ scopes });
  if (newToken) LocalTokenStorage.save(newToken);
};

export const isTokenExpired = (token: string) => {
  const claims = jwtDecode(token);
  if (claims.exp) {
    const curr = Math.floor(Date.now() / 1000);
    return claims.exp < curr;
  }

  return false;
};

const redirect = (path: string) => {
  window.location.href = `${window.location.origin}${path}`;
};

const exchangeLocalStorageToken = async (
  options: AccessTokenExchangeOptions = {
    forceRefresh: false,
    scopes: null,
    noLogOut: false,
  }
) => {
  const jwtToken = await getCognitoJwt({
    forceRefresh: options.forceRefresh,
    noRedirect: options.noLogOut,
  });
  if (!jwtToken) {
    // If no Cognito token, we can't get an authToken, and user is effectively logged out
    if (!options.noLogOut) {
      cleanUserArtifacts();
      window.location.href = AFTER_SIGN_OUT_PATH;
    }
    return null;
  }

  // if we have a Cognito token, exchange it for an authToken
  const scopes = options.scopes || determineScopes();

  try {
    const svcAuthClient = getAuthClient();
    const response = await svcAuthClient.exchangeForAccessToken({
      subjectToken: jwtToken,
      scopes,
    }).promise;
    const data = await response.json();

    if (data?.access_token) {
      return data.access_token;
    }
    throw new Error("No access token returned from Cognito exchange");
  } catch (e) {
    let message = "Error exchanging Cognito token for authToken";
    if (e instanceof Error) {
      message = e.message;
    }
    console.error(message, e);
    throw e;
  }
};

const exchangeCognitoTokenForSvcToken = async (
  options: AccessTokenExchangeOptions = { forceRefresh: false, scopes: null, noLogOut: false }
) => {
  if (getUsingCognitoCookies()) {
    return await exchangeCognitoCookieForSvcToken(options);
  }
  return await exchangeLocalStorageToken(options);
};

export const exchangeCognitoCookieForSvcToken = async (
  options: AccessTokenExchangeOptions = {
    scopes: null,
    noLogOut: false,
  }
) => {
  const svcAuthClient = getAuthClient();
  const scopes = options.scopes || determineScopes();
  try {
    const response = await svcAuthClient.exchangeForAccessToken({ scopes }).promise;
    const data = await response.json();

    if (response.status === 200 && data.access_token) {
      return data.access_token;
    } else if (response.status === 401 && data.message === "id token is expired") {
      const loginClient = getLoginClient();
      const { error: refreshError } = await loginClient.refreshToken();
      if (refreshError) {
        throw new Error(refreshError.message || "Error refreshing token");
      }

      const retryResponse = await svcAuthClient.exchangeForAccessToken({ scopes }).promise;
      const retryData = await retryResponse.json();
      if (retryResponse.status === 200 && retryData.access_token) {
        return retryData.access_token;
      } else {
        throw new Error("No access token in response");
      }
    } else {
      const errorMessage = data?.message || "Error exchanging Cognito token for authToken";

      throw new Error(errorMessage);
    }
  } catch (e: unknown) {
    console.error(e);
    cleanUserArtifacts();
    window.location.assign(SIGNED_OUT_PAGE);
    return null;
  }
};

export const determineScopes = (isSjAdmin?: boolean): Scopes => {
  if (isSjAdmin === undefined) {
    isSjAdmin = getIsSjAdminFromStorage();
  }
  const impersonatedEmail = getImpersonatedEmail();
  const currentSjAdminTenant = getCurrentSelectedTenant();
  const isOperatingInTenant = !!(impersonatedEmail || currentSjAdminTenant);

  return {
    admin: isSjAdmin ? (currentSjAdminTenant ?? undefined) : undefined,
    as: isSjAdmin ? (impersonatedEmail ?? undefined) : undefined,
    sjAdmin: isSjAdmin && !isOperatingInTenant ? true : undefined,
  };
};

export const signOut = async (redirectOverride?: string) => {
  const loggedInFromV2 = getUsingCognitoCookies();
  const isSjAdmin = getIsSjAdminFromStorage();

  try {
    if (loggedInFromV2) {
      await getIdpClient().signOut();
    } else {
      await cognitoGlobalSignOut();
    }
  } catch (error) {
    console.error("Error signing out:", error);
  } finally {
    cleanUserArtifacts();

    if (redirectOverride) {
      redirect(redirectOverride);
    } else if (isSjAdmin) {
      redirect(LoginAppRoutes.routes.sjAdmin.route);
    } else {
      redirect(SIGNED_OUT_PAGE);
    }
  }
};

export const getUserDirectoryUuid = () => {
  const token = LocalTokenStorage.get();
  if (!token) {
    return null;
  }
  return jwtDecode<JwtPayload & AccessTokenClaims>(token).directory_uuid;
};

export const retrieveTenantList = async (): Promise<string[]> => {
  const authToken = await retrieveAuthToken();
  if (!authToken) return [];

  const { available_tenants: tenants } = jwtDecode<JwtPayload & AccessTokenClaims>(authToken);
  return tenants?.map(({ name }) => name) ?? [];
};

export const saveAuthToken = (token: string) => {
  LocalTokenStorage.save(token);
};
